import { useMemo, useState } from 'react';
import { Button, ConfirmDialog, Input, TextArea, useToast } from '@bbpos/ui';
import {
  notificationService,
  type BroadcastSendResponse,
  type NotificationPriority,
} from '@/services/notification.service';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, GLASS, cn } from '@/constants/design-tokens';
import { NotificationCardBody } from '../NotificationCardBody';
import { TenantPicker } from './TenantPicker';
import { BroadcastPriorityBadge } from './BroadcastPriorityBadge';

const PRIORITIES: NotificationPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const TITLE_MAX = 120;
const MESSAGE_MAX = 1000;

type Mode = 'platform' | 'owner' | 'viewer';

interface BroadcastComposeCardProps {
  mode: Mode;
  onSent: () => void;
}

interface TenantOption {
  id: string;
  business_name: string;
}

export function BroadcastComposeCard({ mode, onSent }: BroadcastComposeCardProps) {
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<NotificationPriority>('MEDIUM');
  const [scope, setScope] = useState<'ALL' | 'TENANT'>('ALL');
  const [tenant, setTenant] = useState<TenantOption | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const canPlatformAll = mode === 'platform';
  const canPlatformTarget = mode === 'platform';
  const canOwnerStaff = mode === 'owner';
  const isViewer = mode === 'viewer';

  const validationError = useMemo(() => {
    if (title.trim().length < 3) return 'Title must be at least 3 characters.';
    if (title.length > TITLE_MAX) return `Title cannot exceed ${TITLE_MAX} characters.`;
    if (message.trim().length < 3) return 'Message must be at least 3 characters.';
    if (message.length > MESSAGE_MAX) return `Message cannot exceed ${MESSAGE_MAX} characters.`;
    if (mode === 'platform' && scope === 'TENANT' && !tenant)
      return 'Pick a target tenant before sending.';
    return null;
  }, [title, message, mode, scope, tenant]);

  const scopeSummary: string = useMemo(() => {
    if (mode === 'owner') return 'Every active user in your tenant';
    if (scope === 'ALL') return 'Every active tenant on the platform';
    return tenant ? `Users of ${tenant.business_name}` : 'Select a tenant';
  }, [mode, scope, tenant]);

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setPriority('MEDIUM');
    setScope('ALL');
    setTenant(null);
  };

  const handleSend = async () => {
    setSubmitting(true);
    try {
      let result: BroadcastSendResponse;
      if (mode === 'owner') {
        result = await notificationService.sendTenantAnnouncement({
          title: title.trim(),
          message: message.trim(),
          priority,
        });
      } else {
        result = await notificationService.sendBroadcast({
          title: title.trim(),
          message: message.trim(),
          priority,
          target_tenant_id: scope === 'TENANT' ? tenant?.id : undefined,
        });
      }
      toast({
        title: `Broadcast sent — ${result.recipient_count.toLocaleString('en-IN')} ${
          result.scope === 'PLATFORM' ? 'tenants' : 'users'
        } notified`,
        variant: 'success',
      });
      resetForm();
      setShowConfirm(false);
      onSent();
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to send broadcast',
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isViewer) {
    return (
      <div className={cn(GLASS_FORM, 'p-5 sm:p-6 animate-slide-up')}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
              Read-only access
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 leading-relaxed max-w-prose">
              Your role can review the broadcast history for audit purposes but cannot compose new
              announcements. SUPER_ADMIN / ADMIN send platform broadcasts; OWNER sends tenant-wide
              staff memos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Build a synthetic notification for live preview.
  const previewNotification = {
    id: 'preview',
    tenant_id: 'preview',
    user_id: 'preview',
    type: 'SYSTEM_ANNOUNCEMENT' as const,
    priority,
    title: title.trim() || 'Your announcement title',
    message: message.trim() || 'Recipients will see the body of your message here.',
    is_read: false,
    action_url: null,
    created_at: new Date().toISOString(),
  };

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr] animate-slide-up">
        {/* Composer column */}
        <div className={cn(GLASS_FORM, 'p-5 sm:p-6')}>
          <div className="flex items-center gap-2 mb-5">
            <span className="h-2 w-2 rounded-full bg-violet-500 shadow-[0_0_10px_rgba(139,92,246,0.8)]" />
            <h2 className="text-sm font-bold text-neutral-800 dark:text-neutral-100 uppercase tracking-wider">
              Compose announcement
            </h2>
          </div>

          <div className="space-y-5">
            <div>
              <Input
                label="Title"
                placeholder="e.g. Scheduled maintenance tonight"
                value={title}
                onChange={(v) => setTitle(v.slice(0, TITLE_MAX))}
                disabled={submitting}
                required
              />
              <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1 text-right tabular-nums">
                {title.length}/{TITLE_MAX}
              </p>
            </div>

            <TextArea
              label="Message"
              placeholder="Write a clear, specific announcement. Recipients will see this in-app and — depending on their preferences — over email, SMS, WhatsApp, or push."
              value={message}
              onChange={setMessage}
              rows={6}
              maxLength={MESSAGE_MAX}
              disabled={submitting}
              required
            />

            {/* Priority picker */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                Priority
              </p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Priority">
                {PRIORITIES.map((p) => {
                  const active = p === priority;
                  return (
                    <button
                      key={p}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setPriority(p)}
                      disabled={submitting}
                      className={cn(
                        'rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                        active
                          ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-[0_6px_18px_-8px_rgba(99,102,241,0.6)]'
                          : 'bg-white/70 dark:bg-neutral-800/60 text-neutral-700 dark:text-neutral-200 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700/60 hover:bg-brand-50/60 dark:hover:bg-brand-900/20',
                      )}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Scope picker */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                Scope
              </p>
              {canOwnerStaff ? (
                <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-700/30 px-4 py-3">
                  <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                    My staff — every user in this tenant
                  </p>
                  <p className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">
                    Hard-locked to your tenant. Cannot be sent across tenants.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Scope">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={scope === 'ALL'}
                      onClick={() => setScope('ALL')}
                      disabled={submitting || !canPlatformAll}
                      className={cn(
                        'rounded-full px-4 py-1.5 text-xs font-semibold transition-all',
                        scope === 'ALL'
                          ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-[0_6px_18px_-8px_rgba(79,70,229,0.6)]'
                          : 'bg-white/70 dark:bg-neutral-800/60 text-neutral-700 dark:text-neutral-200 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700/60',
                      )}
                    >
                      All active tenants
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={scope === 'TENANT'}
                      onClick={() => setScope('TENANT')}
                      disabled={submitting || !canPlatformTarget}
                      className={cn(
                        'rounded-full px-4 py-1.5 text-xs font-semibold transition-all',
                        scope === 'TENANT'
                          ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-[0_6px_18px_-8px_rgba(79,70,229,0.6)]'
                          : 'bg-white/70 dark:bg-neutral-800/60 text-neutral-700 dark:text-neutral-200 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700/60',
                      )}
                    >
                      Specific tenant
                    </button>
                  </div>
                  {scope === 'TENANT' && (
                    <TenantPicker value={tenant} onChange={setTenant} disabled={submitting} />
                  )}
                </div>
              )}
            </div>

            {validationError && (
              <p className="text-xs text-rose-600 dark:text-rose-300 font-medium">
                {validationError}
              </p>
            )}

            <div className="flex items-center justify-between gap-4 pt-2">
              <div className="text-[11px] text-neutral-500 dark:text-neutral-400 leading-relaxed">
                Audience: <span className="font-semibold text-neutral-700 dark:text-neutral-200">{scopeSummary}</span>
              </div>
              <Button
                variant="primary"
                size="md"
                onClick={() => setShowConfirm(true)}
                disabled={submitting || !!validationError}
              >
                <svg className="h-4 w-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12l5 5 10-10" />
                </svg>
                Review and send
              </Button>
            </div>
          </div>
        </div>

        {/* Preview column */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Live preview
            </p>
            <BroadcastPriorityBadge priority={priority} />
          </div>
          <div className={cn(GLASS, 'p-4')}>
            <NotificationCardBody
              notification={previewNotification}
              onClick={() => undefined}
            />
          </div>
          <div className={cn(GLASS, 'p-4 space-y-1.5')}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Delivery path
            </p>
            <ul className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" />
                In-app inbox (always on)
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-teal-500 shrink-0" />
                Browser push / email / SMS / WhatsApp — only where the recipient has opted in
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-violet-500 shrink-0" />
                Audit log entry created for every send
              </li>
            </ul>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        variant={priority === 'URGENT' ? 'danger' : 'warning'}
        title={`Send broadcast to ${scopeSummary.toLowerCase()}?`}
        description={`"${title.trim()}" will be delivered immediately and cannot be recalled. Make sure the copy is final.`}
        confirmLabel={submitting ? 'Sending…' : 'Send broadcast'}
        cancelLabel="Keep editing"
        loading={submitting}
        onConfirm={handleSend}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );
}
